/**
 * SEARCH-002 §4.6 — Fetch de l'aperçu d'un hit À LA SÉLECTION uniquement.
 *
 * Débounce léger, cache (TanStack v5 `keepPreviousData`), abort des sélections
 * obsolètes (`signal`), `enabled` au focus virtuel. JAMAIS un fetch pour tous
 * les résultats — l'aperçu est secondaire au listing (ne dégrade pas le SLO).
 *
 * ⚠️ Depuis le 2026-08-11, la palette lit `/search/summary`, le MÊME contrat que
 * le volet de synthèse. Il y avait avant deux endpoints, deux projections et
 * deux présentations pour la même fiche ; leur seule justification écrite était
 * la performance, et la mesure l'a démentie (médiane 0,5 ms pour la synthèse,
 * 0,3–1,2 ms pour l'aperçu — surcoût ~0,2 ms). Ce qui restait, c'était deux
 * écrans à faire diverger.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useDebounce } from '@/components/shared/_referential-picker/useDebounce'
import type { SummaryDetail } from '@/components/summary/types'
import { API_BASE, apiFetch } from '@/lib/api'
import { handleApiError } from '@/lib/api-error'
import { queryKeys } from '@/lib/queryKeys'
import type { SourceType } from '@/lib/searchCategories'

export interface PreviewTarget {
    type: SourceType
    id: string
}

export function useSearchPreview(
    target: PreviewTarget | null,
    entityId: string | null | undefined,
) {
    // Débounce TRÈS léger (40 ms) : ressenti instantané au survol, tout en
    // coalesçant un scroll clavier rapide. Le cache rend instantané tout item
    // déjà survolé ; `keepPreviousData` évite le flicker entre deux survols.
    const debounced = useDebounce(target, 40)

    return useQuery<SummaryDetail>({
        queryKey: queryKeys.search.preview(debounced?.type ?? '', debounced?.id ?? ''),
        queryFn: async ({ signal }) => {
            const params = new URLSearchParams({
                type: debounced!.type,
                id: debounced!.id,
                entity_id: entityId ?? '',
            })
            const res = await apiFetch(`${API_BASE}/search/summary?${params.toString()}`, {
                headers: entityId ? { 'X-Entity-Id': entityId } : undefined,
                signal,
            })
            if (!res.ok) await handleApiError(res)
            return res.json()
        },
        enabled: !!debounced && !!entityId,
        placeholderData: keepPreviousData,
        staleTime: 60_000,
        retry: false,
    })
}
